import { useMemo } from 'react';
import { useMediaQuery, useTheme } from '@material-ui/core';
import { createStyles, makeStyles } from '@material-ui/styles';
import { observer } from 'mobx-react';
import { useHistory } from 'react-router-dom';
import { AppTheme, Colors } from 'styles/theme';
import Flex from 'components/common/Flex';
import { useTranslation } from 'react-i18next';
import useDepsContainer from 'hooks/useDepsContainer';
import { ObjectsSvg } from 'assets/icons';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/css';
import 'swiper/css/effect-creative';
import 'swiper/css/pagination';
import 'swiper/css/navigation';
import 'swiper/swiper-bundle.css';

const Features = () => {
  const styles = useStyles();
  const history = useHistory();
  const theme = useTheme();
  const mdDown = useMediaQuery(theme.breakpoints.down('md'));
  const lgDown = useMediaQuery(theme.breakpoints.down('lg'));
  const { uiStore } = useDepsContainer();
  const { t, i18n } = useTranslation();
  const cachedT = useMemo(() => t, [t, uiStore.language]);

  const IntroCard = ({ index }) => {
    let cardText, cardLabel;
    switch (index) {
      case 1:
        cardText = cachedT('meetInYourLeague');
        cardLabel = cachedT('efficientDating');
        break;
      case 2:
        cardText = cachedT('selfUpgradeAspiration');
        cardLabel = cachedT('attractPopularDaters');
        break;
      case 3:
        cardText = cachedT('safeMeetings');
        cardLabel = cachedT('calmHighReputationDating');
        break;
    }
    return (
      <Flex className={styles.introCard} width={mdDown ? 148 : 252} p={mdDown ? 3 : 4} column>
        <img
          src={`/images/home-rectangle-${index}.png`}
          alt=""
          width={mdDown ? 116 : 212}
          height={mdDown ? 116 : 212}
        />
        <p style={{ fontSize: mdDown ? '16px' : '20px', marginTop: mdDown ? '10px' : '20px' }}>{cardText}</p>
        <span>{cardLabel}</span>
      </Flex>
    );
  };

  const TextCard = () => {
    return (
      <>
        <div
          style={{ fontSize: mdDown ? 40 : 64 }}
          className={mdDown ? styles.reputationTitleMd : lgDown ? styles.reputationTitleLg : styles.reputationTitle}
        >
          <div dangerouslySetInnerHTML={{ __html: t('reputationIsEverything') }} />
        </div>
        <Flex fontSize={mdDown ? 16 : 24} fontWeight={300} mt={mdDown ? 2 : 6} className={styles.reputationDesc}>
          {cachedT('secretGiftsFinancialTools')}
        </Flex>
      </>
    );
  };

  return (
    <Flex className={styles.content} id={'reputation'} width={mdDown ? '100vw' : '100%'} column={mdDown || lgDown}>
      {(mdDown || lgDown) && (
        <Flex px={6} className={styles.featureTxt} center column>
          <TextCard />
        </Flex>
      )}
      <Flex width={'100%'} center={mdDown || lgDown} mt={mdDown || lgDown ? 4 : 0}>
        <Flex pl={mdDown ? 0 : 12} column>
          <Flex mb={12}>
            <IntroCard index={1} />
          </Flex>
          <Flex>
            <IntroCard index={2} />
          </Flex>
        </Flex>
        <Flex pl={mdDown ? 4 : 12} style={{ height: '100%', alignItems: 'center', justifyContent: 'center' }} column>
          <IntroCard index={3} />
        </Flex>
        {!mdDown && !lgDown && (
          <Flex px={12} className={styles.featureTxt} column>
            <TextCard />
          </Flex>
        )}
      </Flex>
    </Flex>
  );
};

const useStyles = makeStyles((theme: AppTheme) =>
  createStyles({
    content: {
      margin: '0 auto',
      display: 'flex',
      justifyContent: 'center',
      maxWidth: '1280px'
    },
    introCard: {
      width: '100%',
      display: 'flex',
      background: 'white',
      borderRadius: '12px',
      color: 'black',
      boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.05)',
      border: '4px solid #000',

      '& p': {
        color: '#18181B',
        fontWeight: '500',
        letterSpacing: '-0.5px',
        fontSize: '20px'
      },
      '& span': {
        fontWeight: '300',
        color: '#5F606D',
        fontSize: '16px'
      },
      '& img': {
        objectFit: 'cover'
      }
    },
    featureTxt: {
      height: '100%',
      alignItems: 'flex-start',
      justifyContent: 'center',
      color: '#232429',
      '& b': {
        color: '#232429',
        marginRight: '5px'
      }
    },
    reputationTitle: {
      color: '#18181B',
      width: '519px',
      // height: '224px',
      fontFamily: 'Lexend',
      fontSize: '64px',
      fontWeight: '900',
      lineHeight: '78px',
      letterSpacing: '-1px',
      marginLeft: '21px',

      '& div': {
        display: 'flex'
      },
      '& em': {
        display: 'none',
        width: '101px',
        height: '101px',
        background: 'url(images/title-icon4.svg) left no-repeat',
        backgroundSize: '101px 101px',
        marginTop: '-51px',
        marginRight: '20px',
        marginLeft: 'auto'
        // clear: 'left'
      }
    },
    reputationTitleMd: {
      color: '#18181B',
      width: '100%',
      // height: '144px',
      fontFamily: 'Lexend',
      fontSize: '40px',
      fontWeight: '900',
      lineHeight: '48px',
      letterSpacing: '-1px',
      marginLeft: '0',
      textAlign: 'center',

      '& em': {
        display: 'inline-block',
        width: '50px',
        height: '50px',
        background: 'url(images/title-icon4.svg) left no-repeat',
        backgroundSize: '50px 50px',
        marginTop: '-21px',
        marginLeft: '-10px'
      }
    },
    reputationTitleLg: {
      color: '#18181B',
      width: '100%',
      fontFamily: 'Lexend',
      fontSize: '64px',
      fontWeight: '900',
      lineHeight: '78px',
      letterSpacing: '-1px',
      margin: '0 auto',

      '& div': {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      },
      '& em': {
        display: 'none',
        width: '101px',
        height: '101px',
        background: 'url(images/title-icon4.svg) left no-repeat',
        backgroundSize: '101px 101px',
        marginTop: '-51px',
        marginRight: '20px',
        marginLeft: 'auto'
        // clear: 'left'
      }
    },
    reputationDesc: {
      marginLeft: '21px'
    }
  })
);

export default observer(Features);
